'use strict';

customerZone.controller('RegistrationController', ['$window', '$scope', '$q', '$log', '$timeout', '$location',
    'utils', 'config', 'AuthenticationService', 'CustomerService', 'ProductService', 'SubscriptionService',
    'SettingsService', 'Session', 'errorCode', 'shippingInfoProvider', 'salesAnalytics', 'OrderService',
    'Storage', 'ControllerBase', 'UnleashedClient',
    function($window, $scope, $q, $log, $timeout, $location, utils, config, AuthenticationService,
        CustomerService, ProductService, SubscriptionService, SettingsService, Session, errorCode,
        shippingInfoProvider, salesAnalytics, OrderService, Storage, ControllerBase, api){

        /*
         * A little note on registration:
         *
         * The registration is presented to the user as a series of steps.  However, from the controller side, we
         * don't really care about the steps, and we just consider the registration to be a set of actions that
         * all need to be performed in order to end up with a registered product.
         *
         * The steps are just a layer on top of that, to streamline the experience and to hide irrelevant parts
         * to the user based on some conditions.
         *
         * The product selection is not part of this controller and is assumed to have taken place before entering
         * the view.  Hence the product is passed in as a search parameter in the location.
         *
         * The different actions that need to be taken to perform a registration are:
         *
         * - Create a new customer account [only if the user does not yet have one]
         * - Log in to an account
         * - Agree to TOS
         * - Pick a topup
         * - Select MNP options (empty for no mnp, else donor operator, iccid and msisdn)
         * - Purchase a subscription + aforementioned topup
         */

        var self = this;

        ControllerBase.call(this, utils);

        var MSISDN_EXISTING = 'existing';
        var SIM_EXISTING = 'existing';

        var HANDLED = 'handled';
        var LOGIN_CANCELLED = 'login_cancelled';

        var FIELD_LOCATIONS = {
            'email': 'personal_data',
            'password': 'personal_data',
            'postcode': 'personal_data',
            'mnp_msisdn': 'mnp_data_step',
            'iccid': 'mnp_data_step'
        };

        self.loading = true;

        self.data = {
            sim: {
                state: SIM_EXISTING
            },
            msisdn: {},
            product: null,
            account: {
                email: '',
                exists: false,
                address: {country: 'be'}
            },
            delivery: {
                useDifferentAddress: false
            },
            validatedPassword: null,
            trafficDataCommunicationAgreed: false
        };

        self.topupsAvailable = [];

        self.activation = {};

        self.uwaValidationErrors = {};

        self.noPreSelection = false;

        self.passwordObfuscated = true;

        self.donorTypes = SubscriptionService.DONOR_TYPES;

        self.isJimNumber = false;
        self.numberHasBeenChecked = false;
        self.checkingMsisdn = false;

        self.tosLabel = '';
        self.trafficDataLabel = '';

        /**
         * Validate that no uwa validation errors exist for the given keys
         */
        self.noUwaErrors = function(){
            for (var i=0; i<arguments.length; i++){
                if (self.uwaValidationErrors[arguments[i]]) return false;
            }
            return true;
        };

        self.downlight = function(){
            angular.element(document.querySelector('#msisdn_form')).removeClass('highlight-required');
        };

        self.isKnownMsisdn = function(field){
            self.isJimNumber = false;
            self.numberHasBeenChecked = false;
            if ( field.$valid ) {
                self.checkingMsisdn = true;
                api.isKnownMsisdn(self.data.msisdn.existing).then(
                    function(response){
                        if (response) self.isJimNumber = true;
                        self.numberHasBeenChecked = true;
                        self.checkingMsisdn = false;
                    }
                );
            }
        };

        self.resetMsisdn = function(){
            self.data.msisdn.existing = undefined;
            self.numberHasBeenChecked = false;
            self.checkingMsisdn = false;
            self.isJimNumber = false;
        };

        /**
         * Go to the step that makes sense for the state of the current order
         */
        function goToOrderStep() {
            if (!self.order) return;
            if (self.order.state === jim.OrderState.waiting_for_payment) {
                self.registrationWizard.goToStep('step_confirm_and_pay');
            }
            else if (self.order.state === jim.OrderState.cancelled ||
                self.order.state === jim.OrderState.failed){
                $log.debug('Order went to state ' + self.order.state);
                utils.showError(utils.format('your_order_was_cancelled__make_a_new_one',
                    {orderId: self.order.id}));
            }
            else if (self.order.state === jim.OrderState.review_needed ||
                self.order.state === jim.OrderState.unknown) {
                $log.debug('Order went to state ' + self.order.state);
                utils.showError(utils.format('something_went_wrong_with_your_order__{orderId}__call_care',
                    {orderId: self.order.id}));
            }
            else {
                salesAnalytics.signalPurchase(self.order);
                self.registrationWizard.finalize();
            }
        }

        /**
         * Redirects user to the migration flow if he has already an active subscription
         * Otherwise load the initial product from the url
         **/
        var init = function(){

            var promises = [];

            var productPromise = loadProduct()
                .then(function(product){
                    self.data.product = product;
                    if (!product.allowsPreexistingSim())
                        self.data.sim.state = 'new';
                    if (!product.allowsMnp())
                        self.data.msisdn.state = 'new';
                    self.tosLabel = utils
                        .format('i_agree_to_tos_{tos_href}{privacy_policy_href}{contract_summary_href}', {
                            'tos_href': utils.legalDoc('terms-and-conditions'),
                            'privacy_policy_href': utils.legalDoc('privacy-policy'),
                            'contract_summary_href': utils.legalDoc('contract-summary/'+product.id)
                        });
                    self.trafficDataLabel = utils.format('traffic_data_notifications_{privacy_policy_href}', {
                        'privacy_policy_href': utils.legalDoc('privacy-policy'),
                    });
                    resetWizard();
                });

            promises.push(productPromise);

            var orderPromise = loadOrder();
            promises.push(orderPromise);

            if (Session.authenticated()){
                self.data.account = cloneProfile(Session.profile());
                self.data.account.exists = true;
                promises.push(loadCommunicationSettings());
                promises.push(productPromise.then(handleVatUser)
                    .then(function(){
                        return $q.all([
                            loadDefaultShippingInfo(),
                            $q.all([productPromise, orderPromise])
                                .then(checkForConflictingData)
                        ]);
                    })
                    .catch(function(error){
                        if (error === LOGIN_CANCELLED) { /* Nothing to do -- it's as if there was no account */ }
                    }));
            }

            promises.push(loadTopupProduct()
                .then(function(product){
                    if (!product){
                        self.noPreSelection = true;
                    }
                    self.data.selectedTopupProduct = product;
                }));

            promises.push(ProductService.getTopupProducts()
                .then(function(products){
                    self.topupsAvailable = products
                        .map(function(product){
                            return {
                                amount: product.price,
                                product: product,
                                bonuses: product.bonus
                            };
                        });
                }));


            self.loading = true;

            $q.all(promises)
                .then(function(){
                    loadProgress();
                    return setupStepTracking();
                })
                .then(function(){
                    self.initialized = true;
                    self.loading = false;
                    goToOrderStep();
                })
                .catch(function(error){
                    self.loading = false;
                    self.error = true;
                    $log.error('Error initializing registrationController (products , topups or options ' +
                        'loading promises rejected', error);
                    utils.showError();
                });
        };

        /**
         * @return a promise to the product we're trying to register.  This can be determined either
         * from the current location, or by using the default value
         */
        var loadProduct = function(){
            var productId = $location.search().product;
            if (productId){
                return ProductService.getProduct(productId)
                    .then(function(product){
                        if (product === null)
                            throw new Error('No such product found: '+productId);
                        return product;
                    });
            }
            else {
                return ProductService.getSubscriptionProducts('prepaid')
                    .then(function(products){
                        if (products.length === 0)
                            throw new Error('Not a single prepaid product found');
                        return products[0];
                    });
            }
        };

        /**
         * TODO: differentiate the comment from the one above.
         * @return a promise to the product we're trying to register.  This can be determined either
         * from the current location, or by using the default value
         */
        var loadTopupProduct = function(){
            var topupProductId = $location.search().topupProduct;

            if (topupProductId){
                return ProductService.getProduct(topupProductId)
                    .then(function(product){
                        if (product === null || !product.typeIs('prepaid/topup'))
                            throw new Error('No such topup product found: '+topupProductId);
                        return product;
                    });
            }
            else {
                self.noPreSelection = true;
                return $q.when();
            }
        };

        var loadOrder = function(){
            var orderId = $location.search().order;

            if (orderId){

                if (!Session.authenticated()){
                    // TODO: use our built-in login form here
                    utils.goToLogin($location.absUrl(), 'you_need_to_be_logged_in_to_continue');
                    return $q.when();
                }

                // SBSS sometimes sticks in the previous state
                // for too long. The only solution, sadly, is to
                // wait a couple of seconds before getting the next
                // update.
                return $timeout(function(){}, 3000)
                    .then(function(){
                        return OrderService.getOrder(orderId);
                    })
                    .then(function(order){
                        self.order = order;
                        self.allowedPaymentMethods = mapPaymentMethods(order);

                        self.data.msisdn = order.mnpDetails ? {
                            state: 'existing',
                            existing: order.mnpDetails.msisdn,
                            iccid: order.mnpDetails.donorIccid,
                            donor: order.mnpDetails.donorOperator,
                            donorType: order.mnpDetails.donorType
                        } : {state: 'new'};

                        var isTopup = function(p){ return p.type === jim.ProductType.topup; };
                        var isSubscription = function(p){
                            return [jim.ProductType.postpaid_subscription,
                                jim.ProductType.prepaid_subscription].includes(p.type);
                        };
                        self.data.product = order.products.filter(isSubscription)[0];
                        self.data.selectedTopupProduct = order.products.filter(isTopup)[0];

                        self.data.termsAgreed = true;

                        //TODO: get the products (subscription, topup, options) out of the order here, and set
                        //them in the controller (product, selectedTopupProduct, selectedOptions)

                        // by wrapping this in a timout, the resulting promise only resolves _after_ the view-model
                        // is repopulated with the order data.
                        return $timeout(order);
                    });
            }
            else return $q.when();
        };

        var cloneProfile = function(profile){
            var result = angular.copy(profile);
            result.birthDate = profile.birthDate ?
                profile.birthDate.clone() : undefined;
            return result;
        };

        var mapPaymentMethods = function(order){

            //TODO: this is a copy from paymentMethodSelector -- we should refactor that somehow

            var LABELS = {
                'KBC/CBC Payment Button': 'KBC/CBC',
                'Belfius Payment Button': 'Belfius'
            };

            var ICONS = {
                'KBC/CBC Payment Button': 'kbc.gif',
                'Belfius Payment Button': 'belfius.gif',
                'Bancontact': 'bancontact.svg',
                'Bitcoin': 'bitcoin.svg',
                'Mastercard': 'mastercard.svg',
                'Paypal': 'paypal.svg',
                'VISA': 'visa.svg'
            };

            if (!order || !order.allowedPaymentMethods) return [];
            return order.allowedPaymentMethods.map(function(name){
                return {
                    id: name,
                    label: LABELS[name] || name,
                    icon: ICONS[name] || (name + '.gif')
                };
            });
        };

        var resetWizard = function(){
            $timeout(function(){
                // we need to do this in the next digest cycle, because
                // the scope needs to be up to date in order for
                // conditional steps to work.
                self.registrationWizard.reset();
            }, 0);
        };

        /**
         * Subcontroller for logging in to an existing account
         */
        self.login = (function(){

            var data = {
                password: null
            };

            var loginDeferred;

            /**
             * Request a user to log in.
             * @param closeOption: the label to use for cancelling the login.  If not passed, 'close_login_popup'
             *                     is used.
             * @param description: an optional description to display in the popup
             * @return a promise that:
             * - resolves with true if the user was logged in
             * - resolves with false if the user cancelled the login
             * - rejects in case of an error
             */
            var login = function(closeOption, description){
                if (loginDeferred) return loginDeferred.promise;
                self.login.closeOption = closeOption || 'close_login_popup';
                self.login.description = description;
                self.login.displayLoginPopup = true;
                loginDeferred = $q.defer();
                return loginDeferred.promise;
            };

            var cancel = function(){
                self.login.displayLoginPopup = false;
                self.login.closeOption = undefined;
                self.login.description = undefined;
                if (loginDeferred)
                    loginDeferred.resolve(false);
                loginDeferred = undefined;
            };

            var resetPassword = function(){
                utils.goToReset(utils.currentPath()+'#'+$location.hash(), self.data.account.email);
            };

            /**
             * Confirm the login attempt and log in with the given credentials.
             * This will update the global session, and also set the token
             * to be used in the sales pipe.
             *
             * @return a Promise that resolves when login is completed, rejects when login is cancelled
             */
            var confirm = function(){
                data.validating = true;
                data.error = null;
                self.data.authToken = null;
                if (!data.password) {
                    data.validating = false;
                    return;
                }
                return AuthenticationService.login(self.data.account.email, data.password)
                    .then(function(){
                        self.data.authToken = Session.token;
                        data.validating = false;
                        var profile = Session.profile();
                        self.data.account = {
                            exists: true,
                            id: profile.id,
                            email: profile.email,
                            title: profile.title,
                            vat: profile.vat,
                            firstName: profile.firstName,
                            lastName: profile.lastName,
                            address: profile.address,
                            company: profile.company,
                            birthDate: profile.birthDate,
                            birthPlace: profile.birthPlace
                        };
                        self.data.validatedPassword = data.password;
                        // note: not returning this promise, since it's acceptable if we do this async.  It's not
                        // important enough to wait for it.
                        loadDefaultShippingInfo();
                        return loadCommunicationSettings();
                    }).then(handleVatUser)
                    .then(checkForConflictingData)
                    .then(function(){
                        self.login.displayLoginPopup = false;
                        if (loginDeferred)
                            loginDeferred.resolve(true);
                        loginDeferred = undefined;
                    })
                    .then(function(){
                        return goToOrderStep();
                    })
                    .catch(function(error){
                        if (errorCode.is(error, errorCode.INVALID_GRANT)){
                            data.validating = false;
                            data.error = 'incorrect_credentials_try_again';
                            self.goBackToEdition('personal_data');
                        }
                        else if (errorCode.is(error, errorCode.LOGIN_BY_EMAIL_IMPOSSIBLE)){
                            data.validating = false;
                            utils.showError('cannot_login_with_email__call_care');
                        }
                        else {
                            if (error === LOGIN_CANCELLED)
                                error = {innerCode: HANDLED};
                            if (loginDeferred)
                                loginDeferred.reject(error);
                            loginDeferred = undefined;
                            throw error;
                        }
                    });
            };

            return {
                data: data,
                confirm: confirm,
                login: login,
                cancel: cancel,
                displayLoginPopup: false,
                resetPassword: resetPassword
            };
        }());

        self.useDifferentEmail = function(){
            self.data.account = {exists: false, address: {country: 'be'}};
            self.order = undefined;
        };

        self.displayPassword = function(){
            return self.data.account.password || self.login.data.password;
        };

        var goToFaultyStepAndShowError = function(error, step, errorMessage){
            self.goBackToEdition(step);
            utils.showError(errorMessage || 'there_are_still_errors_in_form');
            throw {
                code: errorCode.INVALID_FIELDS,
                innerCode: HANDLED,
                goToStep: step,
                error: error
            };
        };

        /**
         * Create a new account and log in as it
         *
         * @return a Promise that resolves when the customer is created and logged in
         */
        var createCustomer = function() {
            self.uwaValidationErrors = {};

            // We can only set this value after login, but login also reads out the setting - so it can
            // end up overwriting the intended value.  So we store it here for keepsakes.
            var trafficDataCommunicationAgreed = self.data.trafficDataCommunicationAgreed;
            return self.recaptcha.execute()
                .then(function(recaptchaResponse){
                    return CustomerService.createCustomer(self.data.account, recaptchaResponse);
                })
                .then(function () {
                    self.data.account.exists = true;
                    self.login.data.password = self.data.account.password;
                    return self.login.confirm();
                })
                .then(function(){
                    return SettingsService.setTrafficDataCommunication(trafficDataCommunicationAgreed);
                })
                .catch(function(error){
                    if (errorCode.is(error, errorCode.EMAIL_ADDRESS_ALREADY_IN_USE)){
                        return self.emailTaken.handle()
                            .then(function(){
                                return updateCustomer();
                            })
                            .catch(function(){
                                self.uwaValidationErrors['email'] = 'email_already_used';
                                goToFaultyStepAndShowError(error, 'personal_data');
                            });
                    }
                    else if (!handleValidationErrors(error, [
                        {code: errorCode.EMAIL_ADDRESS_ALREADY_IN_USE, on: 'email',
                            message: 'email_already_used'},
                        {code: errorCode.EMAIL_ADDRESS_ALREADY_IN_USE, field: 'email', on: 'email',
                            message: 'email_already_used'},
                        {code: errorCode.INVALID_VALUE, field: 'email', on: 'email',
                            message: 'email_invalid'},
                        {code: errorCode.PASSWORD_TOO_COMMON, field: 'password', on: 'password',
                            message: 'password_too_common'},
                        {code: errorCode.PASSWORD_ENTIRELY_NUMERIC, field: 'password', on: 'password',
                            message: 'password_entirely_numeric_error_message'},
                        {code: errorCode.PASSWORD_TOO_SIMILAR, field: 'password', on: 'password',
                            message: 'password_too_similar_error_message'},
                        {code: errorCode.PASSWORD_SIMILAR_TO_DETAILS, field: 'password', on: 'password',
                            message: 'password_too_similar_to_details_error_message'},
                        {code: errorCode.INVALID_VALUE, field: 'postcode', on: 'postcode',
                            message: 'not_a_valid_belgian_postcode'}
                    ])) throw error;
                });
        };

        /**
         * Update the current account
         */
        var updateCustomer = function() {
            self.uwaValidationErrors = {};
            return CustomerService.updateCustomer(self.data.account)
                .then(function(){
                    return SettingsService.setTrafficDataCommunication(self.data.trafficDataCommunicationAgreed);
                })
                .catch(function(error){
                    if (!handleValidationErrors(error, [
                        {code: errorCode.EMAIL_ADDRESS_ALREADY_IN_USE, on: 'email',
                            message: 'email_already_used'},
                        {code: errorCode.INVALID_VALUE, field: 'email', on: 'email',
                            message: 'email_invalid'}])) onError(error);
                });
        };

        self.emailTaken = (function(){

            /*
             * Handle the case of email already taken.  We have the following options
             *
             * - User wants a different email address
             * - User knows his password and authenticates
             * - User forgot his password, a link is sent
             *    - User follows link on this browser, authenticates there, continues onboarding there
             *    - User follows link on other browser, then fills in password here, continues onboarding
             * @return a promise that is resolved if the user succeeds in logging in,
             * or rejects if the user decides to make a new selection.
             */
            var handle = function(){
                $location.hash('submit');
                return self.login.login('use_different_email', 'description_email_taken')
                    .then(function(loginResult){
                        if (!loginResult)
                            throw 'login cancelled';
                    });
            };

            var result = {
                handle: handle,
                optionsDisplayed: false
            };

            return result;
        }());

        /**
         * @return boolean Whether to show company's fields or not.
         * Fields are shown for new accounts and
         *  for existing ones on edition but only if it was filled during account creation
         */
        self.companyDetailsVisible = function(){
            if ( !self.data.product ) return false; // if product is not loaded yet
            if ( !self.data.account.exists && self.data.product.isPostpaid() ) return true;
            if ( self.data.account.vat && self.data.account.company) return true;
            return false;
        };

        self.languageSelectorVisible = function(){
            return config.language.substr(0, 2).toLowerCase() === 'en';
        };

        /**
         * Confirmation function for the 'personal info' step.
         * This will delegate to either create account or login.
         * When the promise is resolved, the data.account is
         * supposed to be completely filled.
         */
        self.confirmAccount = function(){
            if (!self.data.account.exists)
                return createCustomer();
            else
                return updateCustomer();
        };

        /**
         * If the current account already has a subscription or a running order, trigger the appropriate reaction.
         *
         * If we've already created our order, this step is skipped automatically.
         *
         * @return a Promise that resolves when this is done
         */
        var checkForConflictingData = function(){
            if (self.order) return $q.when({});

            return checkPreexistingOrders()
                .then(function(existingOrder){
                    if (existingOrder) return {existingOrder: existingOrder};
                });
        };


        /**
         * If there are existing orders waiting to be paid, inform the user of this
         * @return a promise that resolves after the user has made a selection on what to do with existing orders. It
         * will resolve to true if there is at least one order, false otherwse
         */
        function checkPreexistingOrders() {
            return OrderService.getAllOrdersOfType(jim.OrderType.ORDER_A_SIM)
                .then(function (orders) {
                    if (orders.length > 0) {
                        return $q(function (resolve) {
                            self.existingOrders = orders.map(function(order){
                                if (order.state === jim.OrderState.waiting_for_payment)
                                    return {
                                        data: order,
                                        state: 'existing_order_state__waiting_for_payment',
                                        resumeLabel: 'resume_order__pay',
                                        resume: function(){
                                            $location.search('order', order.id);
                                            self.existingOrders.loading = true;
                                            loadOrder().then(function(){
                                                hideOrderConflictPopup();
                                                resolve();
                                            });
                                        }
                                    };
                                if (order.state === jim.OrderState.waiting_for_sim_matching)
                                    return {
                                        data: order,
                                        state: 'existing_order_state__waiting_for_activation',
                                        resumeLabel: 'resume_order__activate',
                                        resume: function(){
                                            redirectToActivation();
                                        }
                                    };
                                return {
                                    data: order,
                                    state: 'existing_order_state__processing'
                                };
                            });

                            self.startNewOrder = function(){
                                hideOrderConflictPopup();
                                resolve();
                            };
                        }).then(function(){ return true; });
                    }
                    else return false;
                });
            function hideOrderConflictPopup(){ self.existingOrders = undefined; }
        }

        /**
         * Handle the case where the authenticated user has a VAT number.
         * The promise returned is either immediately resolved or unresolvable (the user would navigate away from the
         * page instead). It can only be rejected should the user choose to cancel the login (use a different account).
         * @return A Promise that resolves if there is no conflict, or rejects if the user whishes to cancel the login
         */
        function handleVatUser(){
            if (!self.data.account || !self.data.account.vat) return $q.when({});
            if (self.data.product.allowsVat()) return $q.when({});
            return $q(function(resolve, reject){
                self.unsupportedBusiness = {
                    useDifferentEmail: function(){
                        self.useDifferentEmail();
                        reject(LOGIN_CANCELLED);
                        self.unsupportedBusiness = undefined;
                    },
                    hasSubscription: Session.subscribers().length > 0
                };
            });
        }

        self.confirmNumberStep = function() {
            this.persistProgress();
            if (self.data.msisdn.state === 'swap') {
                utils.showInfo('for_swap_go_here_instead');
                return $q.reject({goToStep: 'step_phone_number'});
            }
        };

        self.askNewSim = function(){
            self.data.sim = {state: 'new'};
            // force the nextStep to happen in the next digest phase, to take model changes into account
            $timeout(self.registrationWizard.nextStep, 1);
        };

        self.registrationRequired = function(){
            return self.data.product && self.data.product.requiresRegistration()
                && self.data.sim.state === SIM_EXISTING;
        };

        /**
         * Will go back to some previous edition step.
         * @param step you want to go back to.
         */
        self.goBackToEdition = function(step){
            self.registrationWizard.goToStep(step);
        };

        /**
         * TODO: mixed onboarding: out of scope for now
         * @return {boolean} false
         */
        self.allowSimStateChoice = function(){
            //return self.data.product && self.data.product.allowsPreexistingSim();
            return false;
        };

        self.topupRequired = function(){
            return self.data.product && self.data.product.requiresRegistration() && self.data.sim.state === 'new';
        };

        self.addTopup = function(topup){
            self.data.selectedTopupProduct = topup.product;
        };

        var findTopup = function(productId) {
            for (var i=0; i<self.topupsAvailable.length; i++){
                if (self.topupsAvailable[i].product.id === productId)
                    return self.topupsAvailable[i].product;
            }
            return null;
        };

        self.addPaymentMethod = function(methodId){
            self.data.selectedPaymentMethod = methodId;
        };

        // update this if the format changes.  It will prevent us from restoring outdated formats from
        // local storage
        var DATA_SCHEMA_VERSION = '2';

        self.persistProgress = function(){

            var formatMoment = function(value){
                if (!value) return '';
                return value.format('YYYY-MM-DD');
            };

            try {
                var data =  {
                    'sim': self.data.sim,
                    'msisdn': self.data.msisdn,
                    'account': {
                        address: self.data.account.address,
                        birthDate: formatMoment(self.data.account.birthDate),
                        birthPlace: self.data.account.birthPlace,
                        email: self.data.account.email,
                        firstName: self.data.account.firstName,
                        lastName: self.data.account.lastName,
                        title: self.data.account.title
                    },
                    'trafficDataCommunicationAgreed': self.data.trafficDataCommunicationAgreed,
                    'delivery': self.data.delivery,
                    'topup': self.data.selectedTopupProduct ? self.data.selectedTopupProduct.id : undefined
                };
                Storage.setObject('onboarding', data);
                Storage.setItem('onboarding__version', DATA_SCHEMA_VERSION);
            }
            catch (error){
                $log.error('Failed to store onboarding progress', error);
            }
        };

        var loadProgress = function(){

            var parseMoment = function(payload){
                if (!payload || payload === '') return undefined;
                return moment.utc(payload);
            };

            try {
                var version = Storage.getItem('onboarding__version');
                var progress = Storage.getObject('onboarding');
                if (version === '1') {
                    if (progress.msisdn && progress.msisdn.donor)
                        progress.msisdn.donor = {id: progress.msisdn.donor};
                    version = '2';
                }
                if (version !== DATA_SCHEMA_VERSION) return;
                if (!progress) return;
                if (progress.sim) self.data.sim = progress.sim;
                if (progress.msisdn) self.data.msisdn = progress.msisdn;
                if (progress.account && progress.account.email!==self.data.account.email) self.data.account = {
                    address: progress.account.address,
                    birthDate: parseMoment(progress.account.birthDate),
                    birthPlace: progress.account.birthPlace,
                    email: progress.account.email,
                    exists: false,
                    firstName: progress.account.firstName,
                    lastName: progress.account.lastName,
                    title: progress.account.title
                };
                if (progress.trafficDataCommunicationAgreed !== undefined)
                    self.data.trafficDataCommunicationAgreed = progress.trafficDataCommunicationAgreed;
                if (progress.delivery) self.data.delivery = progress.delivery;
                if (progress.topup) self.data.selectedTopupProduct = findTopup(progress.topup);
            }
            catch (error){
                $log.error('Failed to load onboarding progress', error);
            }
        };

        var ditchProgress = function(){
            Storage.removeItem('onboarding');
            Storage.removeItem('onboarding__version');
        };

        var setupStepTracking = function(){
            // this needs to happen after the view part is initialized
            return $timeout(function(){
                self.registrationWizard.listeners.push(function(event){
                    if (event === 'enter')
                        $location.hash(self.registrationWizard.step.name);
                });

                if ($location.hash() === '')
                    $location.hash(self.registrationWizard.step.name);

                $scope.$watch(
                    function(){ return $location.hash(); },
                    function(hash){
                        if (hash === 'submit'){
                            // We assume, if we got here, previous attempt resulted in email already in use.
                            // So the password does not matter but it should not be blank.  We use a random
                            // one in the off chance that we're wrong-- we wouldn't want a known password
                            // in the wild.
                            self.data.account.password = utils.randomString(15);
                            self.passwordRepeat = self.data.account.password;
                            self.data.termsAgreed = true;
                            // allow the form to go in the correct state
                            $timeout(function(){
                                self.registrationWizard.goToStep('step_confirm_and_pay');
                            }, 0);
                        }
                        else
                            self.registrationWizard.goToStep(hash);
                    });
            }, 0);
        };

        var confirming;
        self.confirm = function(){
            if (confirming || self.order) return;
            confirming = true;
            self.persistProgress();
            return self.confirmAccount()
                .then(function(){
                    return confirmDelivery();
                }).catch(function (error) {
                    confirming = false;
                    if (!error.innerCode || error.innerCode !== HANDLED){
                        onError(error);
                    }
                    // we are still throwing to block the wizard.
                    throw error;
                });
        };

        self.confirmPayment = function(){
            var getPaymentMethod = function(){
                return $q.when(self.data.selectedPaymentMethod);
            };
            return SubscriptionService.payOutstandingOrder(self.order, getPaymentMethod,
                utils.cleanUrlAsMollieRedirect(utils.currentPath())+'?order='+self.order.id)
                .then(function(paymentResponse){
                    if (paymentResponse.paymentUrl){
                        utils.redirect(paymentResponse.paymentUrl);
                        return $q(function(){});
                    }
                });
        };

        /**
         * Confirm purchase of a new subscription with sim delivery
         * @return {*}
         */
        var confirmDelivery = function(){
            if (self.order) {
                $log.warn('A second attempt was made to create an order');
                return;
            }
            var mnpDetails = self.data.msisdn.state === MSISDN_EXISTING ? {
                msisdn: self.data.msisdn.existing,
                donorIccid: self.data.msisdn.iccid,
                donorOperator: self.data.msisdn.donor,
                donorType: self.data.msisdn.donorType
            } : undefined;

            self.syncDeliveryAddress();

            return SubscriptionService.orderSim(
                self.data.product,
                self.data.selectedTopupProduct,
                mnpDetails,
                self.data.selectedOptions,
                self.data.delivery,
                undefined,
                config.eserviceUrl+'#!/registration/sim_underway?order={ORDER_ID}',
                self.data.validatedPassword
            ).then(function(orderResponse){
                self.order = orderResponse;
                ditchProgress();
                self.allowedPaymentMethods = mapPaymentMethods(self.order);
                if (!orderResponse.allowedPaymentMethods) {
                    salesAnalytics.signalPurchase(self.order);
                    self.registrationWizard.finalize();
                }
            }, function(error){
                if (errorCode.is(error, errorCode.USER_IN_DUNNING)){
                    utils.showError('cannot_order_sim_while_in_dunning');
                    return;
                }
                if (!handleValidationErrors(error, [
                    {code: errorCode.ICCID_ALREADY_IN_USE, on: 'iccid',
                        message: 'iccid_already_in_use'},
                    {code: errorCode.INVALID_VALUE, field: 'iccid', on: 'iccid',
                        message: 'invalid_iccid'},
                    {code: errorCode.MNP_NUMBER_ALREADY_KNOWN, on: 'mnp_msisdn',
                        message: 'invalid_mnp_msisdn_in_use__long'}
                ])) onError(error);
            });
        };

        self.confirmRegistration = function(){
            // TODO: Implement SubscriptionService.orderSubscription when mixed onboarding is going live.
            return SubscriptionService.orderSubscription(
                self.data.product,
                self.data.sim.existing,
                self.data.msisdn.existing,
                self.data.msisdn.iccid,
                self.data.selectedOptions,
                config.eserviceUrl+'#!/registration/activate?iccid='
                        +self.data.sim.existing+'&order={ORDER_ID}&sim=existing',
                self.data.validatedPassword
            )
                .then(function(orderResponse){
                    if (orderResponse.paymentUrl){
                        utils.redirect(orderResponse.paymentUrl);
                    }
                    else {
                        self.registrationWizard.finalize();
                        redirectToActivation(self.data.sim.existing, null, orderResponse.id);
                    }
                }, onError);
        };

        /**
         * Load the default address for the current user (if available)
         */
        var loadDefaultShippingInfo = function(){
            // dev note: there's a race condition here -- if the user gets to change
            // his address before this promise resolves, it will be overwritten.
            // However, that is extremely unlikely.
            if (self.data.delivery.useDifferentAddress) return $q.when();
            return shippingInfoProvider.getDefaultShippingAddress(self.data.account)
                .then(function(shippingInfo){
                    self.data.delivery = shippingInfo;
                    self.data.delivery.useDifferentAddress = !(
                        self.data.delivery.firstName === self.data.account.firstName &&
                        self.data.delivery.lastName === self.data.account.lastName &&
                        self.data.delivery.company === '' &&
                        self.data.delivery.address.street === self.data.account.address.street &&
                        self.data.delivery.address.number === self.data.account.address.number &&
                        self.data.delivery.address.box === self.data.account.address.box &&
                        self.data.delivery.address.postcode === self.data.account.address.postcode &&
                        self.data.delivery.address.city === self.data.account.address.city &&
                        self.data.delivery.address.country === self.data.account.address.country
                    );
                });
        };

        var loadCommunicationSettings = function(){
            if (!Session.authenticated()) {
                self.data.trafficDataCommunicationAgreed = true;
                return $q.when();
            }
            return SettingsService.getTrafficDataCommunication()
                .then(function(value){
                    self.data.trafficDataCommunicationAgreed = value;
                });
        };

        /**
         * Set the delivery address the same as the main address,
         * if this option is selected.
         */
        self.syncDeliveryAddress = function(){
            if (self.data.delivery.useDifferentAddress) {
                if (!self.data.delivery.address)
                    self.data.delivery.address = {country: 'be'};
                return;
            }

            self.data.delivery.address = addTo({}, self.data.account.address);
            self.data.delivery.firstName = self.data.account.firstName;
            self.data.delivery.lastName = self.data.account.lastName;
            self.data.delivery.company = '';
        };

        self.reloadPage = function(){
            $window.location.reload();
        };

        var onError = function(error){
            $log.error('registration onError', error);
            self.error = true;
            utils.showError();
        };

        var redirectToActivation = function(iccid, subscription, order){
            var navigate = $location.path('/registration/activate');
            if (iccid)
                navigate = navigate.search('iccid', iccid);
            if (subscription)
                navigate = navigate.search('subscription', subscription);
            if (order)
                navigate.search('order', order);
        };

        self.goToCustomerZone = function(){
            utils.redirect(config.eserviceUrl+'#!/my_products');
        };

        init();

        function addTo(target, source){
            for (var attribute in source)
                target[attribute] = source[attribute];
            return target;
        }

        self.signalAnalytics = function(stepIdx, stepName, data){
            var products = [];
            if (self.data.product)
                products.push({id: self.data.product.id});
            if (self.data.selectedTopupProduct)
                products.push({id: self.data.selectedTopupProduct.id});
            products.push.apply(products, self.data.selectedOptions);
            salesAnalytics.signalCheckout(stepIdx, stepName, products, data);
        };

        /**
         * Wrapper around the original validation handler. This moves to the location of the first issue before
         * returning it.
         */
        var handleValidationErrors = function(error, knownCases){
            var noOpReporter = {
                showError: function() {},
            };
            var firstIssue = self.handleValidationErrors(error, knownCases, noOpReporter);
            if (firstIssue)
                goToFaultyStepAndShowError(error, FIELD_LOCATIONS[firstIssue.on]);
            return firstIssue;
        };
    }]);
